import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * A bench for the jam jar's collider, one small tower per question.
 *
 * JamJar hands the physics a cylinder only when the jar ends up a disc facing
 * the player, and the mesh cuboid for every other pose — and it decides that
 * from the jar's own axis after the art's own turns AND the authored body
 * rotation, so `lying: true` and `rotationDegX: 90` describe the same jar and
 * must land on the same collider. That is what these towers are for: each one
 * puts a jar somewhere a level would actually put it and lets it settle.
 *
 * Read the towers left to right, front row then back row. Every tower stands in
 * its own column with an empty one beside it, so nothing here leans on anything
 * but what is deliberately underneath it.
 */

const CUBE = 1 as LevelPieceType;
const JAR = 3 as LevelPieceType;

const DEG = Math.PI / 180;

/** One piece of a tower, listed bottom-up in CASES. */
interface Slab
{
  type: LevelPieceType;
  width?: number;
  height?: number;
  lying?: boolean;
  rotationDegX?: number;
  rotationDegY?: number;
  rotationDegZ?: number;
}

interface Case
{
  /** Shown in the console listing and in this file's header, nowhere in game. */
  name: string;
  /** Bottom-up, one entry per grid row. */
  stack: Slab[];
}

/**
 * Front row (layer 0) — a jar meeting a cube, both ways up and both spellings
 * of "on its side".
 */
const FRONT: Case[] = [
  {
    name: "disc by rotation on a cube",
    stack: [ { type: CUBE }, { type: JAR, rotationDegX: 90 } ],
  },
  {
    name: "disc by lying flag on a cube — must match the tower to its left",
    stack: [ { type: CUBE }, { type: JAR, lying: true } ],
  },
  {
    name: "cube on a standing jar",
    stack: [ { type: JAR }, { type: CUBE } ],
  },
  {
    name: "standing jar on a cube",
    stack: [ { type: CUBE }, { type: JAR } ],
  },
  {
    name: "disc on a standing jar",
    stack: [ { type: JAR }, { type: JAR, rotationDegX: 90 } ],
  },
];

/** Back row (layer 1) — jars on jars, and the poses that must NOT go round. */
const BACK: Case[] = [
  {
    name: "cube on a disc",
    stack: [ { type: JAR, rotationDegX: 90 }, { type: CUBE } ],
  },
  {
    name: "disc on a disc",
    stack: [ { type: JAR, lying: true }, { type: JAR, lying: true } ],
  },
  {
    name: "standing jar on a standing jar",
    stack: [ { type: JAR }, { type: JAR } ],
  },
  {
    name: "jar turned onto its side along x — a bar, must stay a cuboid",
    stack: [ { type: CUBE }, { type: JAR, rotationDegZ: 90 }, { type: CUBE } ],
  },
  {
    name: "standing jar yawed 45 — still standing, must stay a cuboid",
    stack: [ { type: CUBE }, { type: JAR, rotationDegY: 45 } ],
  },
];

// Columns 0, 2, 4, 6, 8 hold the towers; the odd ones are left empty so a piece
// that does topple has somewhere to go without knocking its neighbour over.
const COLS = 9;
const COLUMN_PITCH = 2;

// The tallest tower is three high, and every tower is bottom-aligned, so row 0
// is the top of the wall and row ROWS - 1 sits on the table.
const ROWS = 3;

function makeLayer( cases: Case[], first_id: number ): LevelLayer
{
  const grid = Array.from( { length: ROWS }, () => Array( COLS ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = first_id;

  cases.forEach( ( test_case, index ) =>
  {
    const col = index * COLUMN_PITCH;

    test_case.stack.forEach( ( slab, height ) =>
    {
      const row = ROWS - 1 - height;

      grid[row][col] = id;
      pieces.push( {
        id,
        type: slab.type,
        x: col,
        y: row,
        width: slab.width ?? 1,
        height: slab.height ?? 1,
        variant: 0,
        ...( slab.lying ? { lying: true } : {} ),
        ...( slab.rotationDegX === undefined ? {} : { rotationDegX: slab.rotationDegX } ),
        ...( slab.rotationDegY === undefined ? {} : { rotationDegY: slab.rotationDegY } ),
        ...( slab.rotationDegZ === undefined ? {} : { rotationDegZ: slab.rotationDegZ } ),
      } );

      id++;
    } );
  } );

  return { grid, pieces };
}

function countPieces( cases: Case[] ): number
{
  return cases.reduce( ( sum, test_case ) => sum + test_case.stack.length, 0 );
}

const JAR_CASES_LEVEL: LevelConfig = {
  level: 90310,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  layers: [
    makeLayer( FRONT, 1 ),
    makeLayer( BACK, 1 + countPieces( FRONT ) ),
  ],
};

export class JarCasesShowcase
{
  public static readonly LEVEL = JAR_CASES_LEVEL.level;

  /** The tower names, front row then back row, left to right. */
  public static readonly CASE_NAMES: readonly string[] =
    [ ...FRONT, ...BACK ].map( test_case => test_case.name );

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? JAR_CASES_LEVEL : null;
  }
}
